import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Activity,
  LayoutDashboard,
  LogOut,
  type LucideIcon,
  MoreHorizontal,
  Route as RouteIcon,
  Tags,
  TerminalSquare,
  X,
} from 'lucide-react';
import type { SessionInfo } from '@mytag/shared';
import { useAuth } from '@/hooks/useAuth';
import { useHealth } from '@/hooks/queries';
import { Button } from '@/components/ui/primitives';
import { MockBadge } from '@/components/common/indicators';
import { cn } from '@/lib/utils';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
  /** Onglet principal de la bottom nav mobile ; sinon regroupé dans « Plus ». */
  primary?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/', label: 'Tableau de bord', icon: LayoutDashboard, end: true, primary: true },
  { to: '/devices', label: 'Appareils', icon: Tags, primary: true },
  { to: '/history', label: 'Historique', icon: RouteIcon, primary: true },
  { to: '/debug', label: 'Debug / Logs API', icon: TerminalSquare },
];

/** Onglets affichés dans la bottom nav mobile (≤768px). */
const BOTTOM_TABS = NAV_ITEMS.filter((item) => item.primary);
/** Pages secondaires reléguées dans le panneau « Plus » pour ne pas surcharger la bottom nav. */
const MORE_MENU_ITEMS = NAV_ITEMS.filter((item) => !item.primary);

function isNavItemActive(pathname: string, item: NavItem): boolean {
  if (item.end) return pathname === item.to;
  return pathname === item.to || pathname.startsWith(`${item.to}/`);
}

function NavItems({ onNavigate }: { onNavigate?: () => void }): JSX.Element {
  return (
    <nav className="space-y-1">
      {NAV_ITEMS.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              'focus-ring flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
              isActive
                ? 'bg-primary/10 text-primary'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground',
            )
          }
        >
          <item.icon className="h-4 w-4 shrink-0" aria-hidden />
          {item.label}
        </NavLink>
      ))}
    </nav>
  );
}

function Brand(): JSX.Element {
  return (
    <div className="flex items-center gap-2.5">
      <span className="grid h-9 w-9 place-items-center rounded-md bg-foreground text-background">
        <Activity className="h-5 w-5" aria-hidden />
      </span>
      <span className="leading-tight">
        <span className="block text-sm font-semibold">MyTag</span>
        <span className="block text-[11px] text-muted-foreground">SmartTags F8 · Vernal</span>
      </span>
    </div>
  );
}

/** Barre de navigation fixe en bas d'écran, seule navigation principale ≤768px. */
function BottomNav({
  onMoreClick,
  moreOpen,
  moreActive,
}: {
  onMoreClick: () => void;
  moreOpen: boolean;
  moreActive: boolean;
}): JSX.Element {
  return (
    <nav
      aria-label="Navigation principale"
      className="pwa-safe-bottom-nav fixed inset-x-0 bottom-0 z-30 grid grid-cols-4 border-t border-border bg-card shadow-nav md:hidden"
    >
      {BOTTOM_TABS.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          className={({ isActive }) =>
            cn(
              'focus-ring flex h-16 flex-col items-center justify-center gap-1 text-[11px] font-medium transition-colors',
              isActive ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
            )
          }
        >
          <item.icon className="h-5 w-5" aria-hidden />
          <span>{item.label}</span>
        </NavLink>
      ))}
      <button
        type="button"
        aria-label="Plus d’options"
        aria-haspopup="dialog"
        aria-expanded={moreOpen}
        onClick={onMoreClick}
        className={cn(
          'focus-ring flex h-16 flex-col items-center justify-center gap-1 text-[11px] font-medium transition-colors',
          moreActive ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
        )}
      >
        <MoreHorizontal className="h-5 w-5" aria-hidden />
        <span>Plus</span>
      </button>
    </nav>
  );
}

/** Bottom sheet « Plus » : pages secondaires (Debug…) et déconnexion, sans surcharger la bottom nav. */
function MoreSheet({
  session,
  isMock,
  onClose,
  onLogout,
}: {
  session: SessionInfo | null;
  isMock: boolean;
  onClose: () => void;
  onLogout: () => void;
}): JSX.Element {
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeButtonRef.current?.focus();
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-40 md:hidden">
      <div className="absolute inset-0 bg-foreground/30" role="presentation" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Plus d’options"
        className="pwa-safe-bottom-nav absolute inset-x-0 bottom-0 space-y-1 rounded-t-2xl border-t border-border bg-card p-3 shadow-nav"
      >
        <div className="mx-auto h-1 w-10 rounded-full bg-border" aria-hidden />
        <div className="flex items-center justify-between px-2 pb-1 pt-2">
          <div>
            <p className="text-sm font-medium">{session?.username ?? 'Utilisateur connecté'}</p>
            {isMock ? <MockBadge className="mt-1" /> : null}
          </div>
          <Button ref={closeButtonRef} variant="ghost" size="icon" aria-label="Fermer" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        {MORE_MENU_ITEMS.length > 0 ? (
          <nav className="space-y-1 border-t border-border pt-2" aria-label="Autres pages">
            {MORE_MENU_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={onClose}
                className={({ isActive }) =>
                  cn(
                    'focus-ring flex min-h-11 items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors',
                    isActive ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-muted',
                  )
                }
              >
                <item.icon className="h-4 w-4 shrink-0" aria-hidden />
                {item.label}
              </NavLink>
            ))}
          </nav>
        ) : null}

        <div className="border-t border-border pt-2">
          <button
            type="button"
            onClick={onLogout}
            className="focus-ring flex min-h-11 w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-danger transition-colors hover:bg-danger/10"
          >
            <LogOut className="h-4 w-4 shrink-0" aria-hidden />
            Déconnexion
          </button>
        </div>
      </div>
    </div>
  );
}

/** Coquille applicative : sidebar ≥768px, bottom navigation bar fixe ≤768px. */
export function AppShell({ children }: { children: React.ReactNode }): JSX.Element {
  const { session, logout, isMock } = useAuth();
  const { data: health } = useHealth();
  const [moreOpen, setMoreOpen] = useState(false);
  const location = useLocation();

  useEffect(() => setMoreOpen(false), [location.pathname]);

  const moreActive = !BOTTOM_TABS.some((item) => isNavItemActive(location.pathname, item));

  return (
    <div className="flex min-h-full flex-col md:flex-row">
      {/* Sidebar ≥768px (tablette paysage et desktop) */}
      <aside className="hidden w-64 shrink-0 flex-col justify-between border-r border-border bg-card p-4 md:flex">
        <div className="space-y-6">
          <Brand />
          <NavItems />
        </div>
        <div className="space-y-3 text-xs">
          {isMock ? <MockBadge /> : null}
          <div className="space-y-1 rounded-md bg-muted/50 p-3">
            <p className="font-medium">{session?.username}</p>
            <p className="text-muted-foreground">
              {session?.userId ? `ID ${session.userId}` : 'Utilisateur connecté'}
            </p>
            <p className="text-muted-foreground">
              Backend : {health ? `v${health.version}` : '—'}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            icon={<LogOut className="h-3.5 w-3.5" />}
            onClick={() => void logout()}
          >
            Déconnexion
          </Button>
        </div>
      </aside>

      {/* Barre supérieure mobile/tablette ≤768px : identité + statut, la navigation vit dans la bottom nav. */}
      <header className="pwa-safe-header sticky top-0 z-30 flex items-center justify-between gap-3 border-b border-border bg-card/95 py-3 backdrop-blur md:hidden">
        <Brand />
        {isMock ? <MockBadge /> : null}
      </header>

      {moreOpen ? (
        <MoreSheet
          session={session}
          isMock={isMock}
          onClose={() => setMoreOpen(false)}
          onLogout={() => void logout()}
        />
      ) : null}

      <main className="min-w-0 flex-1 bg-background">
        <div className="pwa-safe-content mx-auto w-full max-w-7xl space-y-5 pt-4 sm:p-6">{children}</div>
      </main>

      <BottomNav onMoreClick={() => setMoreOpen((open) => !open)} moreOpen={moreOpen} moreActive={moreActive} />
    </div>
  );
}

/** En-tête de page : titre, description et actions alignées à droite. */
export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: string;
  actions?: React.ReactNode;
}): JSX.Element {
  return (
    <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
      <div className="min-w-0 space-y-1">
        <h1 className="truncate text-xl font-semibold tracking-tight">{title}</h1>
        {description ? <p className="text-sm text-muted-foreground">{description}</p> : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}
